/**
 * FilePreviewModal
 * Fetches a file with auth header, blobs it, and renders PDF in an iframe
 * or image in <img>. Provides a Print button that prints the loaded document.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Loader2, Printer, Download, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FilePreviewModal({ open, onClose, file, employeeId, url }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!open || !file) return;
    let currentUrl = null;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          url || `${API}/employees/${employeeId}/files/${file.id}?inline=true`,
          { responseType: "blob" }
        );
        if (cancelled) return;
        currentUrl = URL.createObjectURL(res.data);
        setBlobUrl(currentUrl);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.detail || "Could not load file");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      setBlobUrl(null);
    };
  }, [open, file, employeeId, url]);

  const handlePrint = () => {
    const ctype = (file?.content_type || "").toLowerCase();
    if (ctype.startsWith("image/")) {
      // Open image in a hidden iframe wrapper so we can print it
      const w = window.open("", "_blank", "width=800,height=900");
      if (!w) return toast.error("Please allow pop-ups to print");
      w.document.write(
        `<html><head><title>${file.filename}</title>
        <style>body{margin:0}img{max-width:100%;display:block;margin:auto}</style>
        </head><body><img src="${blobUrl}" onload="setTimeout(()=>window.print(),300)"/></body></html>`
      );
      w.document.close();
    } else {
      // PDF (or unknown) → print the iframe
      const iframe = iframeRef.current;
      if (iframe?.contentWindow) {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch {
          window.open(blobUrl, "_blank");
        }
      } else {
        window.open(blobUrl, "_blank");
      }
    }
  };

  const handleDownload = () => {
    if (!blobUrl || !file) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = file.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const ctype = (file?.content_type || "").toLowerCase();
  const isImage = ctype.startsWith("image/");
  const isPdf = ctype === "application/pdf" || (file?.filename || "").toLowerCase().endsWith(".pdf");

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col"
        data-testid="file-preview-modal"
      >
        <DialogHeader className="px-4 py-3 border-b flex flex-row items-center justify-between gap-2 space-y-0">
          <DialogTitle className="text-base truncate flex-1 pr-4">
            {file?.filename || "Preview"}
          </DialogTitle>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownload}
              disabled={!blobUrl}
              className="gap-1"
              data-testid="preview-download-btn"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </Button>
            <Button
              size="sm"
              onClick={handlePrint}
              disabled={!blobUrl}
              className="gap-1 bg-blue-600 hover:bg-blue-700"
              data-testid="preview-print-btn"
            >
              <Printer className="w-3.5 h-3.5" /> Print
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-slate-100">
          {loading && (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}
          {error && !loading && (
            <div className="h-full flex items-center justify-center text-sm text-red-600 p-6 text-center">
              {error}
            </div>
          )}
          {blobUrl && !loading && !error && (
            <>
              {isPdf && (
                <iframe
                  ref={iframeRef}
                  title="File preview"
                  src={blobUrl}
                  className="w-full h-full"
                  data-testid="preview-iframe"
                />
              )}
              {isImage && (
                <div className="p-4 flex items-center justify-center min-h-full">
                  <img
                    src={blobUrl}
                    alt={file.filename}
                    className="max-w-full max-h-full object-contain shadow-md bg-white"
                    data-testid="preview-image"
                  />
                </div>
              )}
              {!isPdf && !isImage && (
                <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-slate-600 p-6 text-center">
                  <p>Preview not available for this file type.</p>
                  <Button size="sm" onClick={handleDownload} className="gap-1">
                    <Download className="w-3.5 h-3.5" /> Download instead
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
